import { useEffect, useState } from "react";
import { useUISlice } from "@/models/useUISlice";
import { useAppStore } from "@/controller";
import {
  attachLargeFileUploadListeners,
  largeFileUploader,
} from "@/clients/large-file.plugin";
import ModalContainer from "@/components/ModalContainer";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { config } from "@/controller";

const AlertLargeFileUpload = () => {
  const visible = useUISlice((state) => state.alertLargeFileUpload);
  const { t } = useTranslation();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [retryStatus, setRetryStatus] = useState<{
    attempt: number;
    maxAttempts: number;
    delaySeconds: number;
    isWaitingForNetwork: boolean;
  } | null>(null);
  const isLoading = uploadProgress === 0 || uploadProgress === 100;
  const isRetrying = retryStatus !== null;

  const closeUploadModal = async (params?: {
    cancelNativeUpload?: boolean;
  }) => {
    setUploadProgress(0);
    setRetryStatus(null);

    if (params?.cancelNativeUpload) {
      try {
        await largeFileUploader.cancel();
      } catch (error) {
        console.error("Failed to cancel native file upload:", error);
      }
    }

    if (useUISlice.getState().alertLargeFileUpload === visible) {
      useUISlice.getState().setAlertLargeFileUpload(undefined);
    }
  };

  const _onDismiss = () => {
    void closeUploadModal({ cancelNativeUpload: true });
  };

  useEffect(() => {
    if (!visible) return;

    // Get auth token
    const authToken = useAppStore.getState().userToken;

    if (!authToken) {
      console.error("No auth token available");
      void closeUploadModal();
      if (visible.onError) visible.onError(new Error("Missing auth token"));
      else toast.error(t("alertLargeFileUpload.errorGeneric"));
      return;
    }

    // Reset progress and retry status
    setUploadProgress(0);
    setRetryStatus(null);

    const removeUploadListeners = attachLargeFileUploadListeners({
      onProgress: (data) => {
        setUploadProgress(data.progress);
        setRetryStatus(null); // Clear retry status when progress resumes
      },
      onComplete: async (data) => {
        await closeUploadModal();
        try {
          await visible.onComplete(data.downloadedFileUrl);
        } catch (error) {
          console.error("Post-upload processing failed:", error);
          toast.error(t("alertLargeFileUpload.errorGeneric"));
        }
      },
      onCancel: async () => {
        await closeUploadModal();
        visible.onCancel?.();
      },
      onError: async (data) => {
        console.error("Upload error:", data.error, "Fatal:", data.isFatal);
        if (data.isFatal === true) {
          const errorMessage = data.error?.includes("retries")
            ? t("alertLargeFileUpload.errorRetriesExhausted")
            : t("alertLargeFileUpload.errorGeneric");
          await closeUploadModal();
          if (visible.onError) visible.onError(new Error(data.error));
          else toast.error(errorMessage);
        }
      },
      onRetry: (data) => {
        setRetryStatus(data);
        if (data.isWaitingForNetwork) {
          console.log("Waiting for network connection...");
        } else {
          console.log(
            `Retrying upload (${data.attempt}/${data.maxAttempts}) in ${data.delaySeconds} seconds...`,
          );
        }
      },
    });

    // Start the upload
    largeFileUploader
      .uploadFile({
        fileUrl: visible?.filePath,
        authToken,
        serverUrl: config.apiUrl,
      })
      .catch(async (error) => {
        console.error("Upload error:", error);
        await closeUploadModal();
        if (visible.onError) {
          visible.onError(
            error instanceof Error ? error : new Error("Upload failed"),
          );
        } else {
          toast.error(t("alertLargeFileUpload.errorGeneric"));
        }
      });

    // Cleanup function
    return () => {
      void removeUploadListeners();
    };
  }, [visible]);

  if (!visible) return null;

  // Determine subtitle based on current state
  let subtitle = t("alertLargeFileUpload.uploadingDescription");
  if (isRetrying) {
    if (retryStatus.isWaitingForNetwork) {
      subtitle = t("alertLargeFileUpload.waitingForNetwork");
    } else {
      subtitle = t("alertLargeFileUpload.retrying", {
        attempt: retryStatus.attempt,
        maxAttempts: retryStatus.maxAttempts,
        seconds: retryStatus.delaySeconds,
      });
    }
  }

  return (
    <ModalContainer
      title={visible?.fileName || t("alertLargeFileUpload.uploadingTitle")}
      subtitle={subtitle}
      onDismiss={_onDismiss}
      shouldPersistBackdropClick
      hideDismissIcon
      hideCancel
      modalWidth={400}
    >
      <div className="pt-4">
        {(isLoading || isRetrying) && (
          <div className="flex flex-col items-center justify-center py-6">
            <div className="w-10 h-10 rounded-full border-2 border-[var(--fill-3)] border-t-[var(--accent)] animate-spin" />
            {isRetrying && (
              <p className="mt-4 text-sm text-[var(--text-secondary)] text-center">
                {retryStatus.isWaitingForNetwork
                  ? t("alertLargeFileUpload.checkingConnection")
                  : t("alertLargeFileUpload.preparingRetry")}
              </p>
            )}
          </div>
        )}

        {!isLoading && !isRetrying && (
          <div className="space-y-3">
            {/* Progress bar container */}
            <div className="relative w-full h-2 bg-[var(--fill-2)] hidden- rounded-full overflow-hidden">
              <div
                className="h-full bg-[var(--accent)] rounded-full transition-all duration-300 ease-out"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>

            {/* Progress text */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-[var(--text-primary)]">
                {t("alertLargeFileUpload.uploading", {
                  progress: uploadProgress,
                })}
              </span>
              <span className="text-sm text-[var(--text-secondary)]">
                {uploadProgress}%
              </span>
            </div>
          </div>
        )}
      </div>
    </ModalContainer>
  );
};

export default AlertLargeFileUpload;
